export const clone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

export const generateRandomString = (length: number = 64): string => {
  let str = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    str += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return str;
};

export const bufferToBase64encoded = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let str = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }

  return btoa(str);
};

export const objectToQueryString = (params: Record<string, any>, shouldIncludeQuestionMark: boolean = true): string => {
  if (!params) {
    return '';
  }

  const queryString = Object.keys(params)
    .filter((key) => params[key] !== null && params[key] !== undefined)
    .map((key) => {
      const value = Array.isArray(params[key]) ? params[key].join(',') : params[key];

      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');

  if (!queryString) {
    return '';
  }

  return shouldIncludeQuestionMark ? `?${queryString}` : queryString;
};

export const stringToInteger = (val) => {
  if (Number.isNaN(val)) {
    return null;
  }

  return val;
};

export const normalizeStringValue = (value: any): string => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalizedValue = `${value}`.trim();

  if (!normalizedValue) {
    return null;
  }

  return normalizedValue;
};

export const normalizeTextValue = (value: any): string => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalizedValue = `${value}`;

  if (!normalizedValue.trim()) {
    return null;
  }

  return normalizedValue;
};

const formatDateValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const normalizeDateValue = (value: any): string => {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDateValue(value);
  }

  const normalizedValue = `${value}`.trim();

  if (!normalizedValue) {
    return null;
  }

  const dateMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (dateMatch) {
    return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
  }

  const parsedDate = new Date(normalizedValue);

  if (!Number.isNaN(parsedDate.getTime())) {
    return formatDateValue(parsedDate);
  }

  return normalizedValue;
};

export const normalizeIntegerValue = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalizedValue = +value;

  if (!Number.isInteger(normalizedValue) || normalizedValue <= 0) {
    return null;
  }

  return normalizedValue;
};

export const normalizePositiveInteger = normalizeIntegerValue;

export const normalizePositiveIntegerArray = (values: any): number[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalizedValues: number[] = [];

  for (const value of values) {
    const normalizedValue = normalizePositiveInteger(value);

    if (normalizedValue && !normalizedValues.includes(normalizedValue)) {
      normalizedValues.push(normalizedValue);
    }
  }

  return normalizedValues;
};

export const normalizeFilterIds = (ids: any): number[] | null => {
  const normalizedIds = normalizePositiveIntegerArray(ids);

  return normalizedIds.length ? normalizedIds : null;
};

export const normalizeUnitFilterValues = (units: string[]): string[] | null => {
  if (!Array.isArray(units)) {
    return null;
  }

  const normalizedUnits: string[] = [];

  for (const unit of units) {
    const normalizedUnit = normalizeStringValue(unit);

    if (normalizedUnit && !normalizedUnits.includes(normalizedUnit)) {
      normalizedUnits.push(normalizedUnit);
    }
  }

  return normalizedUnits.length ? normalizedUnits : null;
};

export const downloadCsvFile = (rows: Record<string, any>[], fileName: string): void => {
  if (!Array.isArray(rows) || !rows.length || !fileName) {
    return;
  }

  const headers = Object.keys(rows[0]);

  if (!headers.length) {
    return;
  }

  const csvContent = [headers, ...rows.map((row) => headers.map((header) => row[header]))]
    .map((row) => {
      return row
        .map((value) => {
          const normalizedValue = value === null || value === undefined ? '' : `${value}`;
          const escapedValue = normalizedValue.replace(/"/g, '""');

          return `"${escapedValue}"`;
        })
        .join(',');
    })
    .join('\n');

  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  const objectUrl = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');

  link.href = objectUrl;
  link.download = fileName;

  window.document.body.appendChild(link);

  link.click();
  link.remove();

  window.URL.revokeObjectURL(objectUrl);
};

export const downloadBase64File = (base64Data: string, fileName: string, mimeType: string): void => {
  if (!base64Data || !fileName) {
    return;
  }

  const normalizedBase64Data =
    base64Data.indexOf('base64,') > -1 ? base64Data.slice(base64Data.indexOf('base64,') + 7) : base64Data;

  const binaryString = atob(normalizedBase64Data);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([bytes], {
    type: mimeType || 'application/octet-stream',
  });

  const objectUrl = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');

  link.href = objectUrl;
  link.download = fileName;

  window.document.body.appendChild(link);

  link.click();
  link.remove();

  window.URL.revokeObjectURL(objectUrl);
};
